import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ConfirmModal from '../components/Shared/ConfirmModal';

const TicketDetail = ({ isAgency = false }) => {
  const { id, agencyId } = useParams();
  const navigate = useNavigate();
  const { tasks, clients, agencies, addTaskMessage, updateTask, archiveTask, removeTask } = useAppContext();
  
  const [replyText, setReplyText] = useState('');
  const [replyHours, setReplyHours] = useState('');
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editTaskData, setEditTaskData] = useState({ title: '', priority: 'Medium' });

  const task = tasks.find(t => t.id === id);
  const client = task ? clients.find(c => c.id === task.clientId) : null;
  const agency = agencies.find(a => a.id === agencyId);
  const taskAgency = client?.agencyId ? agencies.find(a => a.id === client.agencyId) : null;

  useEffect(() => {
    if (task) {
      setEditTaskData({ title: task.title, priority: task.priority });
    }
  }, [task, isEditModalOpen]);

  if (!task) return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-20 text-center font-bold text-sm">
      <p className="text-slate-500 mb-4">Task not found.</p>
      <button onClick={() => navigate(-1)} className="text-teal-400 font-bold uppercase">&larr; Back</button>
    </div>
  );

  const handleSend = (e) => {
    e.preventDefault();
    if (!replyText) return;
    const senderName = isAgency && agency ? agency.name : 'Support';
    addTaskMessage(task.id, { sender: senderName, text: replyText }, parseFloat(replyHours) || 0);
    setReplyText('');
    setReplyHours('');
  };

  const handleEditTask = (e) => {
    e.preventDefault();
    updateTask(task.id, { title: editTaskData.title, priority: editTaskData.priority });
    setIsEditModalOpen(false);
  };

  const handleBack = () => {
    if (isAgency && agencyId && client) {
      navigate(`/portal/${agencyId}/client/${client.id}`);
    } else {
      navigate(-1);
    }
  };

  const content = (
    <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start gap-6">
        <div className="min-w-0 flex-1">
          <button onClick={handleBack} className="text-slate-500 hover:text-teal-400 font-bold text-xs sm:text-sm uppercase tracking-widest mb-4 flex items-center gap-2 transition-colors">
            <SafeIcon icon={FiIcons.FiArrowLeft} /> {isAgency ? 'Profile' : 'Queue'}
          </button>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="text-2xl sm:text-3xl font-black text-white uppercase break-words">{task.title}</h1>
            <button onClick={() => setIsEditModalOpen(true)} className="text-slate-500 hover:text-teal-400 transition-colors p-1">
              <SafeIcon icon={FiIcons.FiEdit3} />
            </button>
            <span className={`px-3 py-1 rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-wider shrink-0 ${task.status === 'Open' ? 'bg-teal-500/10 text-teal-400 border border-teal-500/20' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>
              {task.status}
            </span>
            {taskAgency && (
              <span className="px-3 py-1 rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-wider shrink-0 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 flex items-center gap-1.5">
                 <SafeIcon icon={FiIcons.FiBriefcase} />
                 {taskAgency.name}
              </span>
            )}
          </div>
          <p className="text-slate-500 font-bold text-sm uppercase">
            Client: <span className="text-white font-black">{client?.name || 'Unknown'}</span>
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <button 
            onClick={() => updateTask(task.id, { status: task.status === 'Open' ? 'Closed' : 'Open' })}
            className={`flex-1 md:flex-none px-5 py-3 rounded-xl border font-black text-xs sm:text-sm transition-all uppercase ${task.status === 'Open' ? 'border-red-500/20 text-red-400 hover:bg-red-400/5' : 'border-teal-500/20 text-teal-400 hover:bg-teal-400/5'}`}
          >
            {task.status === 'Open' ? 'Resolve' : 'Reopen'}
          </button>
          
          {task.status !== 'Archived' && (
            <button 
              onClick={() => setShowArchiveConfirm(true)} 
              className="p-3 rounded-xl bg-slate-900 border border-amber-500/20 text-amber-500 hover:bg-amber-400/5 transition-all shrink-0"
              title="Archive"
            >
              <SafeIcon icon={FiIcons.FiArchive} />
            </button>
          )}

          {!isAgency && (
            <button 
              onClick={() => setShowDeleteConfirm(true)} 
              className="p-3 rounded-xl bg-slate-900 border border-red-500/20 text-red-500 hover:bg-red-400/5 transition-all shrink-0"
              title="Delete Task"
            >
              <SafeIcon icon={FiIcons.FiTrash2} />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8 items-start">
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-slate-900/50 p-4 sm:p-8 rounded-3xl space-y-8 h-[500px] sm:h-[600px] overflow-y-auto border border-slate-800 scrollbar-hide">
            {(task.messages || []).map(msg => {
              const isSupport = msg.sender === 'Support';
              return (
                <div key={msg.id} className={`flex ${isSupport ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[90%] sm:max-w-[85%] p-5 sm:p-6 rounded-3xl shadow-xl ${isSupport ? 'bg-teal-600 text-white rounded-br-none' : 'bg-slate-800 text-slate-200 border border-slate-700 rounded-bl-none'}`}>
                    <div className="flex flex-wrap justify-between items-center gap-x-6 gap-y-1 mb-3 opacity-60 text-[10px] font-black uppercase tracking-widest">
                      <span>{msg.sender}</span>
                      <span className="shrink-0">{new Date(msg.date).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className="whitespace-pre-wrap leading-relaxed text-sm font-medium">{msg.text}</p>
                    {msg.hours > 0 && (
                      <div className={`mt-4 text-[10px] font-black uppercase py-1.5 px-3 rounded-lg flex items-center gap-2 ${isSupport ? 'bg-teal-700/50' : 'bg-slate-900 border border-slate-700'}`}>
                        <SafeIcon icon={FiIcons.FiClock} /> {msg.hours}h Logged
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {(task.messages || []).length === 0 && (
              <div className="h-full flex items-center justify-center text-slate-600 font-bold text-sm uppercase tracking-widest">
                No updates yet
              </div>
            )}
          </div>

          {task.status !== 'Archived' && (
            <div className="bg-slate-900 p-5 sm:p-6 rounded-3xl border border-slate-800 shadow-2xl">
              <form onSubmit={handleSend} className="space-y-4">
                <textarea className="w-full bg-slate-800 border border-slate-700 rounded-2xl p-4 sm:p-5 text-white focus:ring-2 focus:ring-teal-500 outline-none min-h-[120px] transition-all placeholder:text-slate-600 text-sm font-bold" placeholder="Post internal update..." value={replyText} onChange={e => setReplyText(e.target.value)} />
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-3 bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-700 w-full sm:w-auto">
                    <SafeIcon icon={FiIcons.FiClock} className="text-teal-400 shrink-0" />
                    <input type="number" step="0.5" placeholder="0.0" className="bg-transparent text-white font-black w-12 outline-none text-center text-sm" value={replyHours} onChange={e => setReplyHours(e.target.value)} />
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Hours</span>
                  </div>
                  <button type="submit" className="w-full sm:w-auto bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white px-8 py-3.5 rounded-xl font-black transition-all shadow-lg text-sm uppercase" disabled={!replyText} >
                    Post Update
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-8 bg-slate-900 p-6 rounded-3xl border border-slate-800 font-bold">
          <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6 border-b border-slate-800 pb-4">Task Info</h3>
          <div className="space-y-6">
            <div>
              <p className="text-[10px] font-black text-slate-600 uppercase mb-1 tracking-widest">Total Logged</p>
              <p className="text-3xl font-black text-teal-400">{task.loggedHours.toFixed(1)}h</p>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-600 uppercase mb-1 tracking-widest">Priority</p>
              <p className="text-sm font-black text-slate-300 uppercase">{task.priority || 'Medium'}</p>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-600 uppercase mb-1 tracking-widest">Created Date</p>
              <p className="text-sm font-black text-slate-300 uppercase">{new Date(task.date || new Date()).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-600 uppercase mb-1 tracking-widest">Last Updated</p>
              <p className="text-sm font-black text-slate-300 uppercase">{new Date(task.last_updated || task.date || new Date()).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>
        </div>
      </div>

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setIsEditModalOpen(false)}>
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-black text-white mb-8">Edit Task</h2>
            <form className="space-y-6" onSubmit={handleEditTask}>
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Task Subject</label>
                <input autoFocus className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white outline-none focus:ring-2 focus:ring-teal-500 transition-all font-bold text-sm" value={editTaskData.title} onChange={e => setEditTaskData({ ...editTaskData, title: e.target.value })} required />
              </div>
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Priority</label>
                <select className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white outline-none focus:ring-2 focus:ring-teal-500 transition-all font-bold text-sm appearance-none" value={editTaskData.priority} onChange={e => setEditTaskData({ ...editTaskData, priority: e.target.value })}>
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                </select>
              </div>
              <div className="flex flex-col gap-3 pt-6">
                <button type="submit" className="w-full px-4 py-3.5 bg-teal-600 text-white rounded-xl font-black hover:bg-teal-500 shadow-lg shadow-teal-900/40 text-sm uppercase">Save Changes</button>
                <button type="button" onClick={() => setIsEditModalOpen(false)} className="w-full px-6 py-2 text-slate-500 hover:text-white font-bold text-sm uppercase">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal isOpen={showArchiveConfirm} onClose={() => setShowArchiveConfirm(false)} onConfirm={() => { archiveTask(task.id); navigate(isAgency ? `/portal/${agencyId}/client/${client.id}` : '/tasks'); }} title="Archive Task?" message="Hide this task from active utilization?" />
      <ConfirmModal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={() => { removeTask(task.id); navigate(isAgency ? `/portal/${agencyId}/client/${client.id}` : '/tasks'); }} title="Delete Task?" message="Permanently delete this task? This action cannot be undone." />
    </div>
  );

  return isAgency ? (
    <div className="min-h-screen bg-slate-950 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {content}
      </div>
    </div>
  ) : content;
};

export default TicketDetail;